import { FormikConfig } from 'formik';
import { ProfileForm } from './types';
import { profileValidationHash } from './validation';
import { useL10n } from '@uplift/hooks';
import { UseProfileFormHandlersParams, useProfileFormHandlers } from '../profile.utils';
import { useProfileContext } from '../profile.context';
import { patchProviderPractice, patchUserProfile, savePracticeLocation, saveProviderQualificationDegree, updateProviderCommunityExperties, updateProviderEthnicities, updateProviderFaiths, updateProviderGenders, updateProviderLanguages, updateProviderModalities, updateProviderSpecialties, updateProviderSupportedAgeGroups, updateProviderTherapeuticStyles, updateProviderTreatmentApproaches } from '@uplift/api';
import { useWithAddressVerify } from '@uplift/ui-v2';
import isEqual from 'lodash/isEqual';
import { SeeClientsEnum } from '@uplift/types';
import { GENDER_SELF_DESCRIBE_ID } from './GenderForm.component';
import { useMemo } from 'react';
import { useLanguagesQuery } from '@uplift/queries';

type ProfileFormKeys = keyof ProfileForm;
export type ProfileListFormKeys = 'practiceAddress' | 'education';

type OnSubmitHash = {
  [Key in ProfileFormKeys]: Key extends ProfileListFormKeys ? (editIndex: number) => FormikConfig<ProfileForm[Key]>['onSubmit'] : FormikConfig<ProfileForm[Key]>['onSubmit'];
};

type InitialValuesHash = {
  [Key in ProfileFormKeys]: FormikConfig<ProfileForm[Key]>['initialValues'];
};


type ProfileFormikConfig<Key extends ProfileFormKeys> = {
  initialValues: InitialValuesHash[Key];
  onSubmit: FormikConfig<ProfileForm[Key]>['validationSchema'];
  validationSchema: FormikConfig<ProfileForm[Key]>['validationSchema'];
  isInitialValid?: boolean | undefined;
};

type GetFormikConfig = <Key extends Exclude<ProfileFormKeys, ProfileListFormKeys>>(key: Key) => ProfileFormikConfig<Key>;
type GetListFormikConfig = <Key extends ProfileListFormKeys>(key: Key, editIndex: number) => ProfileFormikConfig<Key>;

export const getDegreeFromDegreeValue = (degree: string) => {
  return {
    "Bachelor's": 'bachelors',
    "Master's": 'masters',
    'Doctorate': 'doctorate',
    "Associate's": 'associates',
  }[degree] ?? undefined;
};

export type UseProfileFormikConfigsParams = UseProfileFormHandlersParams;

export const useProfileFormikConfigs = (params?: UseProfileFormikConfigsParams) => {
  const l10n = useL10n();
  const { withFormHandlers } = useProfileFormHandlers(params);
  const withAddressVerify = useWithAddressVerify();
  const { profile } = useProfileContext();
  const { data: languages, isLoading: loading } = useLanguagesQuery();
  const english = useMemo(() => {
    if (loading) {
      return;
    }
    return languages.find((language) => language.name === 'English')?.id;
  }, [ languages, loading ]);
  const spokenLanguages = profile.languages.length === 0 ? [ english ] : profile.languages.map(l => l.languageId);

  const profileInitialValuesHash: InitialValuesHash = {
    preferredName: { preferredName: profile.profile.preferredName ?? '' },
    pronouns: { pronouns: profile.profile.pronouns ?? '' },
    gender: { genders: profile.genders.map(g => g.genderId ?? GENDER_SELF_DESCRIBE_ID), otherGender: profile.genders.find(g => !g.genderId)?.name },
    faith: { faiths: profile.faiths.map(f => f.faithId) },
    ethnicity: { ethnicities: profile.ethnicities.map(e => e.ethnicityId) },
    languages: { spokenLanguages: spokenLanguages },
    bio: { bio: profile.profile.shortBio ?? '' },
    practiceAddress: {
      practiceName: profile.practice.practiceName ?? '',
      addresses: profile.locations.map((practiceLocation): ProfileForm['practiceAddress']['addresses'][number] => {
        return {
          address1: practiceLocation.address.line1,
          address2: practiceLocation.address.line2 ?? null,
          state: practiceLocation.address.stateId,
          city: practiceLocation.address.city,
          zip: practiceLocation.address.zip,
          displayStreetAddress: !practiceLocation.streetAddressHidden,
          locationId: practiceLocation.id,
        };
      })
    },
    sessionOption: {
      locations: profile.locations.map((location) => ({
        locationId: location.id,
        practiceId: location.practiceId,
        seeClientsType: location.practiceServiceType,
        handicapAccessible: location.isHandicapAccessible ?? null,
      }))
    },
    supportedAgeGroups: { supportedAgeGroups: profile.supportedAgeGroups.map(a => a.supportedAgeGroupId) },
    modalities: { therapyTypes: profile.modalities.map((modality) => modality.modalityId) },
    specialties: { specialties: profile.specialties.map(s => s.specialtyId) },
    communityExpertise: {
      faiths: profile.communityExpertise.faiths.map(f => f.faithId),
      ethnicities: profile.communityExpertise.ethnicities.map(e => e.ethnicityId),
      general: profile.communityExpertise.general.map(g => g.expertiseId),
    },
    treatmentApproach: { treatmentApproaches: profile.treatmentApproaches.map(t => t.treatmentApproachId) },
    therapeuticStyle: { therapeuticStyles: profile.therapeuticStyles.map(t => t.therapeuticStyleId) },
    education: {
      degrees: profile.qualification.degrees.map(q => ({
        school: q.school,
        yearGraduated: String(q.yearGraduated),
        degree: q.degree,
        fieldOfStudy: q.fieldOfStudy,
        id: q.id,
      }))
    },
  };


  const profileOnSubmitHash: OnSubmitHash = {
    preferredName: withFormHandlers(({ preferredName }) => patchUserProfile(profile.user.id, { preferredName })),
    pronouns: withFormHandlers(({ pronouns }) => patchUserProfile(profile.user.id, { pronouns })),
    gender: withFormHandlers(({ genders, otherGender }) => {
      const selfDescribed = genders.includes(GENDER_SELF_DESCRIBE_ID) ? [ otherGender ] : undefined;

      return updateProviderGenders({ gender_ids: genders.filter(genderId => genderId !== GENDER_SELF_DESCRIBE_ID), genders_free_text: selfDescribed });
    }),
    faith: withFormHandlers(({ faiths }) => updateProviderFaiths({ faith_ids: faiths })),
    ethnicity: withFormHandlers(({ ethnicities }) => updateProviderEthnicities({ ethnicity_ids: ethnicities })),
    languages: withFormHandlers(({ spokenLanguages }) => updateProviderLanguages({ language_ids: spokenLanguages })),
    bio: withFormHandlers(({ bio }) => patchUserProfile(profile.user.id, { shortBio: bio })),
    practiceAddress: (editIndex) => withAddressVerify(withFormHandlers(async (values) => {
      const practiceName = values.practiceName;
      const practiceAddress = values.addresses[editIndex];
      const currentLocation = profile.locations.find(l => l.id === practiceAddress.locationId);

      if (practiceName !== profileInitialValuesHash['practiceAddress'].practiceName) {
        await patchProviderPractice(profile.user.id, { name: practiceName });
      }

      await savePracticeLocation({
        replaceLocationId: practiceAddress.locationId,

        streetAddressHidden: !practiceAddress.displayStreetAddress,
        address: {
          line1: practiceAddress.address1,
          line2: practiceAddress.address2 ?? null,
          city: practiceAddress.city,
          stateId: practiceAddress.state,
          zip: practiceAddress.zip,
        },

        inPersonVisits: currentLocation?.inPersonVisits ?? false,
        phone: currentLocation?.phone,
        phoneExt: currentLocation?.phoneExt,
        practiceServiceType: currentLocation?.practiceServiceType ?? SeeClientsEnum.telehealth,
        isHandicapAccessible: currentLocation?.isHandicapAccessible ?? null,
      });
    }), {
      shouldCheck: true,
      initialAddress: profileInitialValuesHash.practiceAddress.addresses[editIndex] ?? {
        address1: '',
        address2: null,
        city: '',
        state: '',
        zip: ''
      },
      getAddressFromFormikValues: (formikValues) => formikValues.addresses[editIndex],
    }),
    sessionOption: withFormHandlers(async ({ locations }) => {
      const initialLocations = profileInitialValuesHash.sessionOption.locations;

      await Promise.all(locations.map(async (location, idx) => {
        const isUpdated = !isEqual(location, initialLocations[idx]);

        if (isUpdated) {
          const currentLocation = profile.locations.find(l => l.id === location.locationId);

          if (currentLocation) {
            await savePracticeLocation({
              replaceLocationId: location.locationId,
              address: currentLocation.address,
              inPersonVisits: currentLocation.inPersonVisits,
              streetAddressHidden: currentLocation.streetAddressHidden,
              phone: currentLocation.phone,
              phoneExt: currentLocation.phoneExt,
              practiceServiceType: location.seeClientsType,
              isHandicapAccessible: location.handicapAccessible,
            });
          }
        }
      }));
    }),
    supportedAgeGroups: withFormHandlers(({ supportedAgeGroups }) => updateProviderSupportedAgeGroups({ age_group_ids: supportedAgeGroups })),
    modalities: withFormHandlers(({ therapyTypes }) => updateProviderModalities({ modality_ids: therapyTypes })),
    specialties: withFormHandlers(({ specialties }) => updateProviderSpecialties({ specialty_ids: specialties })),
    communityExpertise: withFormHandlers(async ({ ethnicities, faiths, general }) => updateProviderCommunityExperties({ ethnicity_ids: ethnicities, faith_ids: faiths, community_expertise_general_ids: general })),
    treatmentApproach: withFormHandlers(({ treatmentApproaches }) => updateProviderTreatmentApproaches({ treatment_approach_ids: treatmentApproaches })),
    therapeuticStyle: withFormHandlers(({ therapeuticStyles }) => updateProviderTherapeuticStyles({ therapeutic_style_ids: therapeuticStyles })),
    education: (editIndex) => withFormHandlers(async ({ degrees }) => {
      const { id, ...degree } = degrees[editIndex];

      await saveProviderQualificationDegree({
        ...degree,
        replaceQualificationDegreeId: id,
        yearGraduated: Number(degree.yearGraduated),
        degree: getDegreeFromDegreeValue(degree.degree),
      });
    }),
  };

  const getFormikConfig: GetFormikConfig = (key) => {
    const initialValues = profileInitialValuesHash[key];
    const onSubmit = profileOnSubmitHash[key];
    const validationSchema = profileValidationHash[key](l10n);

    return { initialValues, onSubmit, validationSchema };
  };

  const getListFormikConfig: GetListFormikConfig = (key, editIndex) => {
    const initialValues = profileInitialValuesHash[key];
    const onSubmit = profileOnSubmitHash[key](editIndex);
    const validationSchema = profileValidationHash[key](l10n);

    return { initialValues, onSubmit, validationSchema, isInitialValid: false };
  };


  return { getFormikConfig, getListFormikConfig };
};
